/**
 * Die Angaben einer Instanz, die im Verbund mit der Hauptinstanz angelegt
 * wird (bryve oder eLibrary zu ojfm, oder ojfm zu einem der beiden).
 *
 * Gemeinsam mit der Hauptinstanz sind Kunde, Server, Betriebsart und Umgebung.
 * Je Instanz eigen sind Adresse, Stack-Name, Betreiberkonto und — falls die
 * Anwendung welche verlangt — ihre Einstellungen (eLibrary: Image-Tag).
 */
import { useState } from 'react';
import { Globe } from 'lucide-react';
import DomainAuswahl from './DomainAuswahl';
import ProduktKonfigFelder from './ProduktKonfigFelder';
import type { ProductConfigField } from '../types';

export interface VerbundZusatz {
  slug: string;
  domainQuelle: 'unsere' | 'kunde';
  domain: string;
  subdomain: string;
  subdomainVonHand: boolean;
  stackName: string;
  stackVonHand: boolean;
  adminEmail: string;
  adminPasswort: string;
  konfig: Record<string, string>;
  /** Einstellungen schon mit den Vorgaben des Produkts gefüllt? */
  konfigVorbelegt: boolean;
}

type DomainZeile = { id: string; name: string; cloudflareDetected?: boolean; cloudflareTunnel?: boolean };

export default function VerbundZusatzFelder({
  zusatz,
  setzen,
  name,
  domainListe,
  konfigFelder,
  konfigFehlt,
  adminSecretKey,
  passwortErzeugen,
  kollision,
}: {
  zusatz: VerbundZusatz;
  setzen: (teil: Partial<VerbundZusatz>) => void;
  /** Anzeigename, z. B. „bryve" */
  name: string;
  domainListe: DomainZeile[];
  konfigFelder: ProductConfigField[];
  konfigFehlt: string[];
  /** Kennt die Vorlage ein Betreiberkonto? Sonst kein Abschnitt dafür. */
  adminSecretKey: string | null;
  passwortErzeugen: () => string;
  /** Hinweis, wenn Adresse oder Stack-Name schon vergeben sind */
  kollision: string | null;
}) {
  const [alleDomains, setAlleDomains] = useState(false);
  const [sichtbar, setSichtbar] = useState(false);
  const cloudflare = domainListe.filter((d) => d.cloudflareDetected || d.cloudflareTunnel);
  const adresse = [zusatz.subdomain, zusatz.domain].filter(Boolean).join('.');
  const feld = 'w-full rounded border border-slate-300 px-2 py-1.5 font-mono text-xs focus:outline-none focus:ring-2 focus:ring-brand-500';

  return (
    <div className="space-y-3">
      {konfigFelder.some((f) => f.visibleInPC) && (
        <div className="rounded-lg border border-slate-200 p-3">
          <div className="mb-2 text-xs font-semibold text-slate-600">Einstellungen — {name}</div>
          <ProduktKonfigFelder felder={konfigFelder} werte={zusatz.konfig} setWerte={(w) => setzen({ konfig: w })} />
          {konfigFehlt.length > 0 && <p className="mt-2 text-xs text-amber-700">Noch offen: {konfigFehlt.join(', ')}</p>}
        </div>
      )}

      {adminSecretKey && (
        <div className="rounded-lg border border-slate-200 p-3">
          <div className="text-xs font-medium text-slate-700">
            Betreiberkonto — {name}{' '}
            <span className="font-normal text-slate-400">— unser Weg hinein, wenn der Kunde anruft</span>
          </div>
          <label className="mt-2 block">
            <span className="text-xs text-slate-500">Adresse</span>
            <input type="email" value={zusatz.adminEmail} onChange={(e) => setzen({ adminEmail: e.target.value })} placeholder="admin@juni.com" className={`mt-1 ${feld}`} />
          </label>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <input
              type={sichtbar ? 'text' : 'password'}
              value={zusatz.adminPasswort}
              onChange={(e) => setzen({ adminPasswort: e.target.value })}
              spellCheck={false}
              autoComplete="new-password"
              className="min-w-56 flex-1 rounded border border-slate-300 px-2 py-1.5 font-mono text-xs focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
            <button type="button" onClick={() => setSichtbar((v) => !v)} className="rounded border border-slate-300 px-2 py-1.5 text-xs text-slate-600 hover:bg-slate-50">
              {sichtbar ? 'verbergen' : 'zeigen'}
            </button>
            <button type="button" onClick={() => setzen({ adminPasswort: passwortErzeugen() })} className="rounded border border-slate-300 px-2 py-1.5 text-xs text-slate-600 hover:bg-slate-50">
              neu würfeln
            </button>
            <button
              type="button"
              onClick={() => { navigator.clipboard?.writeText(zusatz.adminPasswort).catch(() => {}); }}
              className="rounded border border-slate-300 px-2 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
            >
              kopieren
            </button>
          </div>
          <p className="mt-2 text-xs leading-snug text-slate-400">
            Landet als <code className="font-mono">{adminSecretKey}</code> in den Geheimnissen der Instanz.{' '}
            <strong>Jetzt notieren</strong> — danach wird es nicht mehr angezeigt.
          </p>
        </div>
      )}

      <div className="rounded-lg border border-slate-200 p-3">
        <p className="mb-2 flex items-center gap-1.5 text-xs font-medium text-slate-600">
          <Globe size={13} className="text-slate-400" /> Adresse — {name}
        </p>
        <div className="mb-2 flex gap-1">
          {(['unsere', 'kunde'] as const).map((q) => (
            <button
              key={q}
              type="button"
              onClick={() => setzen({ domainQuelle: q, domain: '' })}
              className={`rounded px-2.5 py-1 text-xs font-medium ${
                zusatz.domainQuelle === q ? 'bg-brand-600 text-white' : 'border border-slate-300 text-slate-600 hover:bg-slate-50'
              }`}
            >
              {q === 'unsere' ? 'Unsere Domain' : 'Domain des Kunden'}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-3 gap-3">
          <label className="col-span-2 block">
            <span className="mb-1 block text-xs text-slate-500">Domain</span>
            {zusatz.domainQuelle === 'unsere' ? (
              <DomainAuswahl
                domains={alleDomains ? domainListe : cloudflare}
                wert={zusatz.domain}
                onChange={(d) => setzen({ domain: d })}
                nurCloudflare={!alleDomains}
                onNurCloudflareChange={(nur) => setAlleDomains(!nur)}
                gesamtAnzahl={domainListe.length}
                cloudflareAnzahl={cloudflare.length}
              />
            ) : (
              <input value={zusatz.domain} onChange={(e) => setzen({ domain: e.target.value })} placeholder="verlag-kunde.de" className={feld} />
            )}
          </label>
          <label className="block">
            <span className="mb-1 block text-xs text-slate-500">Subdomain</span>
            <input value={zusatz.subdomain} onChange={(e) => setzen({ subdomain: e.target.value, subdomainVonHand: true })} placeholder="kunde" className={feld} />
          </label>
        </div>
        {adresse && (
          <p className="mt-2 text-xs text-slate-600">
            Erreichbar unter <span className="font-mono font-medium text-slate-800">{adresse}</span>
          </p>
        )}
      </div>

      <label className="block">
        <span className="mb-1 block text-xs text-slate-500">Stack-Name — {name} *</span>
        <input value={zusatz.stackName} onChange={(e) => setzen({ stackName: e.target.value, stackVonHand: true })} placeholder="kunde-prod-app" className={feld} />
      </label>

      {kollision && <p className="rounded bg-amber-50 px-2 py-1.5 text-xs text-amber-800">{kollision}</p>}
    </div>
  );
}
